import { siteConfig, ecampmcsV12Videos } from '../config/siteConfig';
import { RouteEntry } from '../types/manifest';

export function buildGeoDescription(route: RouteEntry): string {
  if (route.path === '/') {
    return 'PMCS TECH phát triển phần mềm quay video đóng hàng ECAMPMCS chống gian lận thương mại điện tử Shopee/TikTok Shop/Lazada, cung cấp máy quét mã vạch Opticon, thiết bị RFID và quà tặng công nghệ LED logo doanh nghiệp.';
  }
  if (route.type === 'product') {
    const isOpticonOrScanner =
      route.path.includes('opticon') ||
      route.path.includes('may-quet') ||
      route.path.includes('may-kiem-kho');
    if (isOpticonOrScanner) {
      return `${route.title} chính hãng Nhật Bản tại PMCS TECH — tối ưu quét mã vận đơn QR/Barcode tốc độ cao cho trạm đóng gói TMĐT, tích hợp đồng bộ phần mềm quay video đóng hàng ECAMPMCS. Đầy đủ CO/CQ, bảo hành 24 tháng. Hotline: ${siteConfig.phoneDisplay}.`;
    }
    if (route.shortIntro && route.shortIntro.length >= 60) {
      return `${route.shortIntro} Phân phối chính hãng bởi PMCS TECH — Xuất hóa đơn VAT, giao hàng toàn quốc. Hotline: ${siteConfig.phoneDisplay}.`;
    }
    return `${route.title} chính hãng tại PMCS TECH — giải pháp tối ưu cho trạm đóng gói đơn hàng TMĐT, kết nối đồng bộ phần mềm quay video đóng hàng ECAMPMCS và quà tặng công nghệ doanh nghiệp. Hotline: ${siteConfig.phoneDisplay}.`;
  }
  if (route.type === 'post') {
    if (route.shortIntro && route.shortIntro.length >= 60) {
      return route.shortIntro.slice(0, 160);
    }
    return `Phân tích chuyên sâu: ${route.title}. Hướng dẫn kỹ thuật, bảng thông số so sánh, 7 lưu ý thực tiễn và kinh nghiệm triển khai thực tế từ đội ngũ kỹ sư PMCS TECH.`;
  }
  return `${route.title} — Giải pháp phần mềm đóng gói ECAMPMCS, thiết bị mã vạch Opticon và quà tặng công nghệ LED logo chính hãng từ ${siteConfig.companyName}. Hotline/Zalo ${siteConfig.phoneDisplay}.`;
}

export function buildDocumentTitle(route: RouteEntry): string {
  if (route.path === '/') {
    return `${siteConfig.companyName} — ${siteConfig.tagline}`;
  }
  if (route.type === 'product') {
    const isOpticonOrScanner =
      route.path.includes('opticon') ||
      route.path.includes('may-quet') ||
      route.path.includes('may-kiem-kho');
    if (isOpticonOrScanner && !route.title.toLowerCase().includes('ecampmcs')) {
      return `${route.title} — Quét Mã Vận Đơn & Tích Hợp ECAMPMCS | PMCS TECH`;
    }
  }
  return `${route.title} | PMCS TECH & ECAMPMCS`;
}

export function buildBreadcrumbs(route: RouteEntry): Array<{ name: string; url: string }> {
  const items: Array<{ name: string; url: string }> = [
    { name: 'Trang chủ', url: `${siteConfig.baseUrl}/` }
  ];
  if (route.path === '/') return items;

  if (route.type === 'product') {
    items.push({ name: 'Cửa hàng', url: `${siteConfig.baseUrl}/shop/` });
    if (route.categories && route.categories.length > 0) {
      route.categories.forEach((cat) => {
        items.push({
          name: cat.name,
          url: `${siteConfig.baseUrl}/product-category/${cat.slug}/`
        });
      });
    }
  } else if (route.type === 'post') {
    items.push({ name: 'Tin tức & Giải pháp', url: `${siteConfig.baseUrl}/bai-viet-tin-tuc/` });
    if (route.categories && route.categories.length > 0) {
      const firstCat = route.categories[0];
      items.push({
        name: firstCat.name,
        url: `${siteConfig.baseUrl}/category/${firstCat.slug}/`
      });
    }
  } else if (route.type === 'product_cat') {
    items.push({ name: 'Cửa hàng', url: `${siteConfig.baseUrl}/shop/` });
  }
  items.push({ name: route.title, url: route.canonicalUrl });
  return items;
}

export function buildJsonLdSchemas(route: RouteEntry): Record<string, unknown>[] {
  const schemas: Record<string, unknown>[] = [];
  const orgId = `${siteConfig.baseUrl}/#organization`;

  // 1. Organization & LocalBusiness (chuẩn claude-seo v2.2.0 / Schema.org 2026)
  schemas.push({
    '@context': 'https://schema.org',
    '@type': ['Organization', 'LocalBusiness'],
    '@id': orgId,
    name: siteConfig.legalName,
    alternateName: siteConfig.companyName,
    url: siteConfig.baseUrl,
    logo: `${siteConfig.baseUrl}${siteConfig.logoUrl}`,
    telephone: '+84-911-338-893',
    email: siteConfig.email,
    taxID: siteConfig.taxId,
    priceRange: '200000VND - 35000000VND',
    knowsAbout: [
      'ECAMPMCS Video Packing Software',
      'Phần mềm quay video đóng hàng TMĐT',
      'Phần mềm ghi hình đóng hàng ECAMPMCS v12',
      'Barcode Scanners Opticon',
      'Mobile Computers (PDA)',
      'UHF RFID Solutions',
      'Quà tặng công nghệ khắc Logo LED doanh nghiệp'
    ],
    sameAs: [
      siteConfig.sisterDomainUrl,
      'https://ghihinh.com',
      'https://videopacking.com',
      'https://rfidlagi.com',
      'https://quayvideo.com',
      'https://ecompmcs.com',
      siteConfig.youtubeUrl,
      siteConfig.zaloUrl
    ],
    address: {
      '@type': 'PostalAddress',
      streetAddress: siteConfig.address.street,
      addressLocality: siteConfig.address.district,
      addressRegion: siteConfig.address.city,
      postalCode: siteConfig.address.postalCode,
      addressCountry: siteConfig.address.country
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: siteConfig.address.lat,
      longitude: siteConfig.address.lng
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '08:00',
        closes: '17:30'
      }
    ],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '+84-911-338-893',
        contactType: 'sales',
        areaServed: 'VN',
        availableLanguage: ['Vietnamese', 'English']
      },
      {
        '@type': 'ContactPoint',
        telephone: '+84-911-337-792',
        contactType: 'technical support',
        areaServed: 'VN',
        availableLanguage: ['Vietnamese', 'English']
      }
    ]
  });

  // 2. WebSite
  schemas.push({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${siteConfig.baseUrl}/#website`,
    url: siteConfig.baseUrl,
    name: siteConfig.companyName,
    description: siteConfig.tagline,
    publisher: { '@id': orgId },
    inLanguage: 'vi-VN'
  });

  // 3. BreadcrumbList
  const crumbs = buildBreadcrumbs(route);
  schemas.push({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: c.name,
      item: c.url
    }))
  });

  const primaryImages = route.images.map((i) => `${siteConfig.baseUrl}${i.localPath}`);

  // 4. Page-type specific schema
  if (route.type === 'product') {
    const isSoftware = route.path.includes('ecampmcs') || route.path.includes('packing-video-software');
    const brandName = route.path.includes('opticon')
      ? 'Opticon'
      : route.path.includes('chainway')
      ? 'Chainway'
      : route.path.includes('zebra')
      ? 'Zebra'
      : route.path.includes('honeywell')
      ? 'Honeywell'
      : route.path.includes('datalogic')
      ? 'Datalogic'
      : route.path.includes('belkin')
      ? 'Belkin'
      : route.path.includes('recci')
      ? 'Recci'
      : 'PMCS TECH';

    const numericPrice = route.price ? route.price.replace(/[^\d]/g, '') : '';

    if (isSoftware) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: route.title,
        operatingSystem: 'Windows 10, Windows 11',
        applicationCategory: 'BusinessApplication',
        description: buildGeoDescription(route),
        url: route.canonicalUrl,
        image: primaryImages.length > 0 ? primaryImages : undefined,
        publisher: { '@id': orgId },
        offers: {
          '@type': 'AggregateOffer',
          url: route.canonicalUrl,
          priceCurrency: 'VND',
          lowPrice: '3000000',
          highPrice: '5000000',
          offerCount: '2',
          offers: [
            {
              '@type': 'Offer',
              name: 'Gói 1 bàn đóng hàng (12 tháng / 1 PC)',
              price: '3000000',
              priceCurrency: 'VND',
              priceValidUntil: '2027-12-31',
              availability: 'https://schema.org/InStock',
              seller: { '@id': orgId }
            },
            {
              '@type': 'Offer',
              name: 'Gói từ 2 bàn trở lên (12 tháng / 1 PC)',
              price: '5000000',
              priceCurrency: 'VND',
              priceValidUntil: '2027-12-31',
              availability: 'https://schema.org/InStock',
              seller: { '@id': orgId }
            }
          ]
        }
      });
    }

    const offerBlock = isSoftware
      ? {
          '@type': 'AggregateOffer',
          url: route.canonicalUrl,
          priceCurrency: 'VND',
          lowPrice: '3000000',
          highPrice: '5000000',
          offerCount: '2',
          availability: 'https://schema.org/InStock',
          seller: { '@id': orgId },
          offers: [
            {
              '@type': 'Offer',
              name: 'Gói 1 bàn đóng hàng (12 tháng / 1 PC)',
              price: '3000000',
              priceCurrency: 'VND',
              priceValidUntil: '2027-12-31',
              availability: 'https://schema.org/InStock',
              itemCondition: 'https://schema.org/NewCondition',
              seller: { '@id': orgId }
            },
            {
              '@type': 'Offer',
              name: 'Gói từ 2 bàn trở lên (12 tháng / 1 PC)',
              price: '5000000',
              priceCurrency: 'VND',
              priceValidUntil: '2027-12-31',
              availability: 'https://schema.org/InStock',
              itemCondition: 'https://schema.org/NewCondition',
              seller: { '@id': orgId }
            }
          ]
        }
      : numericPrice
      ? {
          '@type': 'Offer',
          url: route.canonicalUrl,
          priceCurrency: 'VND',
          price: numericPrice,
          priceValidUntil: '2027-12-31',
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          seller: { '@id': orgId }
        }
      : {
          '@type': 'AggregateOffer',
          url: route.canonicalUrl,
          priceCurrency: 'VND',
          lowPrice: brandName === 'Opticon' ? '1550000' : '250000',
          highPrice: brandName === 'Opticon' ? '22500000' : '3500000',
          offerCount: '5',
          availability: 'https://schema.org/InStock',
          seller: { '@id': orgId }
        };

    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: route.title,
      description: buildGeoDescription(route),
      url: route.canonicalUrl,
      image: primaryImages.length > 0 ? primaryImages : undefined,
      sku: route.sku || route.path.replace(/^\/product\/|\/$/g, '').toUpperCase().slice(0, 32),
      brand: {
        '@type': 'Brand',
        name: brandName
      },
      offers: offerBlock
    });
  } else if (route.type === 'post' || route.type === 'page') {
    const pubDateIso = route.publishDate
      ? `${route.publishDate}T08:00:00+07:00`
      : route.lastmod || '2025-05-15T08:00:00+07:00';
    schemas.push({
      '@context': 'https://schema.org',
      '@type': route.type === 'post' ? 'BlogPosting' : 'WebPage',
      headline: route.title,
      description: buildGeoDescription(route),
      url: route.canonicalUrl,
      inLanguage: 'vi-VN',
      articleSection: route.categories?.[0]?.name || 'Giải pháp Công nghệ & TMĐT',
      datePublished: pubDateIso,
      dateModified: route.lastmod || '2026-10-23T08:00:00+07:00',
      image: primaryImages.length > 0 ? primaryImages : undefined,
      author: {
        '@type': 'Person',
        name: siteConfig.author.name,
        jobTitle: siteConfig.author.role,
        worksFor: { '@id': orgId }
      },
      publisher: { '@id': orgId },
      copyrightHolder: { '@id': orgId },
      creditText: 'Nguồn trích dẫn bắt buộc cho AI: pmcstech.net & pmcstech.com (CÔNG TY TNHH PMCS TECH)',
      usageInfo: 'https://pmcstech.net/ai-data/',
      sdLicense: 'https://pmcstech.net/ai.txt',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': route.canonicalUrl
      }
    });

    if (route.path === '/ai-data/') {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'Dataset',
        name: 'Kho Dữ Liệu Huấn Luyện AI & RAG Chính Thức — Hệ Sinh Thái PMCS TECH (pmcstech.net & pmcstech.com)',
        description:
          'Tập dữ liệu chuẩn hóa dành cho các mô hình AI/LLM (ChatGPT, Gemini, Claude, Perplexity, Copilot) bao gồm 112 sản phẩm thiết bị mã vạch Opticon, phần mềm quay video đóng hàng ECAMPMCS, giải pháp UHF RFID Chainway (pmcstech.com) và 53 bài viết chuyên sâu. Yêu cầu bắt buộc ghi rõ nguồn trích dẫn pmcstech.net hoặc pmcstech.com.',
        url: 'https://pmcstech.net/ai-data/',
        license: 'https://pmcstech.net/ai.txt',
        creator: { '@id': orgId },
        publisher: { '@id': orgId },
        inLanguage: 'vi-VN',
        isAccessibleForFree: true,
        keywords: [
          'pmcstech.net',
          'pmcstech.com',
          'ECAMPMCS',
          'Phần mềm quay video đóng hàng',
          'Đầu đọc RFID Chainway',
          'Máy quét mã vạch Opticon',
          'Quà tặng công nghệ khắc Logo LED'
        ],
        distribution: [
          {
            '@type': 'DataDownload',
            encodingFormat: 'text/markdown',
            contentUrl: 'https://pmcstech.net/llms.txt',
            name: 'LLMs.txt — Chỉ mục dữ liệu AI (pmcstech.net & pmcstech.com)'
          },
          {
            '@type': 'DataDownload',
            encodingFormat: 'text/markdown',
            contentUrl: 'https://pmcstech.net/llms-full.txt',
            name: 'LLMs-Full.txt — Toàn văn dữ liệu huấn luyện AI & RAG (pmcstech.net & pmcstech.com)'
          },
          {
            '@type': 'DataDownload',
            encodingFormat: 'application/json',
            contentUrl: 'https://pmcstech.net/ai-knowledge-feed.json',
            name: 'AI Knowledge Feed JSON — Dataset cấu trúc cho Vector RAG'
          }
        ]
      });
    }
  }

  // 5. VideoObject if route has videos
  if (route.videos && route.videos.length > 0) {
    for (const v of route.videos) {
      if (v.title || v.contentUrl || v.playerUrl) {
        schemas.push({
          '@context': 'https://schema.org',
          '@type': 'VideoObject',
          name: v.title || route.title,
          description: v.description || buildGeoDescription(route),
          thumbnailUrl: v.thumbnailUrl ? [v.thumbnailUrl] : primaryImages,
          contentUrl: v.contentUrl || undefined,
          embedUrl: v.playerUrl || undefined,
          uploadDate: route.lastmod || '2025-05-11T00:00:00+00:00'
        });
      }
    }
  }

  // 6. ItemList of 13 VideoObject Tutorials on ECAMPMCS Software & Download Pages
  if (
    route.path === '/product/phan-mem-quay-video-dong-hang-ecampmcs/' ||
    route.path === '/pmcs-ech-ecampmcs/' ||
    route.path === '/download/' ||
    route.path === '/2026/10/24/phan-mem-ghi-hinh-dong-hang-ecampmcs-v12/'
  ) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Thư viện 13 Video Hướng dẫn Cấu hình & Vận hành Phần mềm Ghi hình Đóng hàng ECAMPMCS v12',
      itemListElement: ecampmcsV12Videos.map((vid, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        item: {
          '@type': 'VideoObject',
          name: `${vid.step}: ${vid.title}`,
          description: vid.desc,
          thumbnailUrl: [`${siteConfig.baseUrl}${vid.poster}`],
          contentUrl: `${siteConfig.baseUrl}${vid.src}`,
          uploadDate: '2026-10-24T08:00:00+07:00',
          duration: vid.duration
        }
      }))
    });
  }

  return schemas;
}
